import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';

import { conectarWhatsapp, fetchConfiguracao, type ConexaoWhatsapp } from '@/api/secretario';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

/** O QR do WhatsApp expira em cerca de 45 s; pedimos um novo antes disso. */
const RENOVAR_QR_MS = 40_000;
/** Enquanto o QR está na tela, a configuração é reconsultada para ver o canal nascer. */
const CONSULTAR_MS = 3_000;

interface ConectarPorQrProps {
  /** O canal da configuração já está conectado (por esta ou outra forma). */
  conectado: boolean;
  /** O número caiu: o botão diz "Reconectar". */
  reconectar: boolean;
  /** Avisa o passo quando há QR na tela: quem anuncia a conexão é ele, que continua montado. */
  onAguardandoChange?: (aguardando: boolean) => void;
}

/**
 * Conexão do número por QR code, como no WhatsApp Web (Evolution API —
 * docs/PLANO_SECRETARIO_WHATSAPP_EVOLUTION.md, Bloco B). Não é oficial do
 * WhatsApp: exige o aceite do aviso de risco.
 */
export function ConectarPorQr({ conectado, reconectar, onAguardandoChange }: ConectarPorQrProps) {
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const [aceite, setAceite] = useState(false);
  const [conexao, setConexao] = useState<ConexaoWhatsapp | null>(null);
  const qrNaTela = conexao !== null && !conexao.conectado;
  const aguardando = qrNaTela && !conectado;

  // Enquanto o QR está na tela, a configuração é reconsultada: o canal nasce no
  // servidor quando a Evolution confirma a leitura.
  useQuery({
    queryKey: queryKeys.secretario.config,
    queryFn: fetchConfiguracao,
    refetchInterval: (query) =>
      qrNaTela && query.state.data?.canal?.status !== 'CONECTADO' ? CONSULTAR_MS : false,
  });

  const conectar = useMutation({
    mutationFn: conectarWhatsapp,
    onSuccess: (dados) => {
      setConexao(dados);
      if (dados.conectado) queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
    },
    onError: (error) => {
      setConexao(null);
      notifyMutationError(error, showError);
    },
  });

  // O QR expira: enquanto ninguém lê, pede outro. O ``mutate`` do React Query é estável.
  const { mutate: pedirQr } = conectar;
  useEffect(() => {
    if (!aguardando) return undefined;
    const timer = window.setInterval(() => pedirQr(), RENOVAR_QR_MS);
    return () => window.clearInterval(timer);
  }, [aguardando, pedirQr]);

  useEffect(() => {
    onAguardandoChange?.(aguardando);
  }, [aguardando, onAguardandoChange]);

  if (aguardando) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-lg border p-4 sm:flex-row sm:items-start">
        {conexao?.qr_base64 ? (
          <img
            src={conexao.qr_base64}
            alt="QR code para conectar o WhatsApp"
            className="h-56 w-56 shrink-0 rounded-md bg-white p-2"
          />
        ) : (
          <div className="flex h-56 w-56 shrink-0 items-center justify-center rounded-md border">
            <Spinner size={32} label="Gerando o QR code" />
          </div>
        )}
        <div className="space-y-3 text-sm">
          <p className="font-medium">No celular com o número de atendimento:</p>
          <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
            <li>Abra o WhatsApp.</li>
            <li>
              Toque em <span className="font-medium text-foreground">Aparelhos conectados</span> e depois em{' '}
              <span className="font-medium text-foreground">Conectar aparelho</span>.
            </li>
            <li>Aponte a câmera para este código.</li>
          </ol>
          <p className="flex items-center gap-2 text-muted-foreground">
            <Spinner size={16} className="text-inherit" />
            Aguardando a leitura. O código se renova sozinho.
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => setConexao(null)}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border p-4">
        <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="space-y-1 text-sm">
          <p className="font-medium">Conecte o WhatsApp que você usa para atender clientes</p>
          <p className="text-muted-foreground">
            O Secretário passa a responder por esse número, como um aparelho conectado. Você continua usando o WhatsApp
            no celular normalmente. Prefira um número só de trabalho, de preferência no WhatsApp Business.
          </p>
        </div>
      </div>
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Antes de conectar</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            Se você usa o WhatsApp Business, desligue a mensagem de saudação e a de ausência: elas saem do seu celular e o
            Secretário as entenderia como você mesmo respondendo.<br></br>
            Se o celular ficar muito tempo sem internet, a conexão cai e avisaremos você para ler o código de novo.
          </p>
          <div className="flex items-center gap-3 pt-1">
            <Switch id="aceite-risco" checked={aceite} onCheckedChange={setAceite} />
            <Label htmlFor="aceite-risco" className="text-sm font-normal text-foreground">
              Entendi e quero conectar meu número
            </Label>
          </div>
        </AlertDescription>
      </Alert>
      <Button type="button" onClick={() => conectar.mutate()} disabled={!aceite || conectar.isPending}>
        {conectar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
        {reconectar ? 'Reconectar meu WhatsApp' : 'Conectar meu WhatsApp'}
      </Button>
    </div>
  );
}
